import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import { useState } from 'react'
import { authApi } from '~/entities/session/api/auth'
import { AuthHeading, AuthShell, authLinkClassName } from '~/features/auth/auth-shell'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { forgotPasswordSchema } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: (search.email as string) || '',
  }),
})

function ForgotPasswordPage() {
  const { email: searchEmail } = Route.useSearch()
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)

  const sendResetCode = useMutation({
    mutationFn: (email: string) => authApi.sendPasswordResetCode(email),
    onSuccess: (_data, email) => {
      setSubmittedEmail(email)
    },
  })

  const form = useForm({
    defaultValues: {
      email: searchEmail,
    },
    onSubmit: async ({ value }) => {
      await sendResetCode.mutateAsync(value.email)
    },
  })

  if (submittedEmail) {
    return (
      <AuthShell
        footer={
          <p>
            {m.didntReceiveEmail()}{' '}
            <button
              type="button"
              onClick={() => setSubmittedEmail(null)}
              className={authLinkClassName}
            >
              {m.tryAgain()}
            </button>
          </p>
        }
      >
        <AuthHeading
          icon={<Mail />}
          title={m.checkYourEmail()}
          description={m.passwordResetEmailSent({ email: submittedEmail })}
        />
        <p className="text-muted-foreground text-sm">{m.passwordResetEmailInstructions()}</p>
        <Button asChild variant="brand" size="lg" className="w-full">
          <Link to="/login" search={{ email: submittedEmail, reason: '' }}>
            {m.backToLogin()}
          </Link>
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      footer={
        <p>
          {m.rememberYourPassword()}{' '}
          <Link
            to="/login"
            search={{ email: form.state.values.email, reason: '' }}
            className={authLinkClassName}
          >
            {m.signIn()}
          </Link>
        </p>
      }
    >
      <AuthHeading
        title={m.forgotPassword1()}
        description={m.enterYourEmailToReceivePasswordResetInstructions()}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-4"
      >
        <form.Field
          name="email"
          validators={createFieldValidators(forgotPasswordSchema.shape.email)}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.email()}</Label>
              <Input
                id={field.name}
                type="email"
                placeholder={m.businessexamplecom()}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                autoFocus
              />
              {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                <p className="text-destructive text-sm">
                  {typeof field.state.meta.errors[0] === 'string'
                    ? field.state.meta.errors[0]
                    : m.invalidValue()}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {sendResetCode.isError && (
          <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">
              {sendResetCode.error instanceof Error
                ? sendResetCode.error.message
                : m.failedToSendResetEmailPleaseTryAgain()}
            </p>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={sendResetCode.isPending}
        >
          {sendResetCode.isPending ? m.sending() : m.sendResetEmail()}
        </Button>
      </form>
    </AuthShell>
  )
}
